"use client"

import { useState, useEffect } from "react"
import { FaBars, FaTimes } from "react-icons/fa"
import { Link } from "react-router-dom"
import { Globe, Bell } from "lucide-react"
import Logo from "../../assets/images/logo/hazer-logo.png"
import menuItems from "../../utils/menu-items"

import tokenService from "../../services/token-service"
import ProfileDropdown from "../ui/profile-dropdown"

const Header = () => {
  const [isOpen, setIsOpen] = useState(false)
  const [authUser, setAuthUser] = useState(null)
  const [isScrolled, setIsScrolled] = useState(false)

  useEffect(() => {
    const user = localStorage.getItem("authUser")

    if (user) {
      setAuthUser(JSON.parse(user))
    }

    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20)
    }
    window.addEventListener("scroll", handleScroll)
    return () => window.removeEventListener("scroll", handleScroll)
  }, [])
  const toggleNavbar = () => {
    setIsOpen(!isOpen)
  }

  const getDashboardLinks = () => {
    if (!authUser || !authUser.role) return []
    
    const roleName = authUser.role.name
    const dashboards = []

    if (roleName === "customer") {
      dashboards.push({ label: "Customer Dashboard", url: "/customer/dashboard" })
    } else if (roleName === "candidate" || roleName === "marketing_executive") {
      dashboards.push({ label: "Employee Dashboard", url: "/emp/dashboard" })
    } else if (roleName === "handyman") {
      dashboards.push({ label: "Handyman Dashboard", url: "/handyman/dashboard" })
    }

    return dashboards
  }

  const getMenuItemsForRole = () => {
    const roleName = authUser?.role?.name?.toLowerCase()

    if (!roleName) return menuItems

    let allowedLabels = null

    if (roleName === "candidate") {
      allowedLabels = ["Home","Jobs","About", "Contact","Designs"]
    } else if (roleName === "customer") {
      allowedLabels = ["Home","Services","Goods","About", "Contact","Designs"]
    }
     else if (roleName === "handyman") {
      allowedLabels = ["Home","About", "Contact","Designs"]
    } else if (roleName === "vendor") {
      allowedLabels = [ "Home","Goods","About", "Contact","Designs"]
    }

    if (!allowedLabels) return menuItems

    const labelToItem = new Map(menuItems.map((item) => [item.label, item]))
    return allowedLabels.map((label) => labelToItem.get(label)).filter(Boolean)
  }

  const handleLogout = () => {
    tokenService.removeToken();
    tokenService.removeUserDetails();
    tokenService.clearAll();
    setAuthUser(null)
  }

  return (
    <header className="fixed top-0 w-full z-50 transition-all duration-300 bg-brand-primary shadow-[0_15px_50px_rgba(59,130,246,0.4),_0_10px_30px_rgba(245,166,35,0.3)] border-b border-white/10">
      {/* Download Section Style Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Orange and Light Blue blurred drop shadows/glows inside */}
        <div className="absolute -top-[100px] -right-[100px] w-[400px] h-[400px] bg-[#F5A623] rounded-full blur-[90px] opacity-20"></div>
        <div className="absolute -bottom-[50px] -left-[50px] w-[400px] h-[400px] bg-blue-300 rounded-full blur-[90px] opacity-20"></div>
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10 mix-blend-overlay"></div>
        {/* Downside blending gradient into hero */}
        <div className="absolute bottom-0 left-0 w-full h-8 bg-gradient-to-t from-[#EEF5FF]/10 to-transparent"></div>
      </div>
      
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />

      <div className="flex container mx-auto px-8 md:px-12 items-center justify-between py-4 min-h-[88px] relative z-10">
        <div className="pl-4">
  <a
    href="/"
    className="
      inline-flex
      items-center
      rounded-2xl
      px-5
      py-3
      bg-[#EEF5FF]
      border border-[#D9E8FF]
      shadow-[0_10px_30px_rgba(15,23,42,0.08)]
      transition-all
      duration-300
      hover:-translate-y-0.5
      hover:shadow-[0_16px_40px_rgba(37,99,235,0.15)]
    "
  >
    <img
      src={Logo}
      alt="Hazer"
      className="h-14 w-auto transition-transform duration-300 hover:scale-105"
    />
  </a>
</div>
       
        {/* Desktop Navigation */}
        <div className="md:flex sm:hidden flex-1 justify-center">
          <ul className="flex items-center gap-10">
            {getMenuItemsForRole().map((item) => (
              <li key={item.url} className="relative group">
                <Link to={item.url} className="font-medium text-base text-white/90 transition-all duration-250 hover:text-white inline-block transform hover:scale-[1.02]">
                  {item.label}
                </Link>
                {/* Animated sliding underline in Orange */}
                <span className="absolute -bottom-1.5 left-0 w-0 h-0.5 bg-[#F5A623] rounded-full transition-all duration-250 ease-out group-hover:w-full"></span>
              </li>
            ))}
          </ul>
        </div>

        {/* Desktop Auth Buttons or User Menu */}
        <div className="md:flex items-center gap-6 sm:hidden pr-4">
          {/* Action Group: Language & Notifications */}
          <div className="flex items-center gap-3 pr-6 border-r border-white/20">
            <button className="flex items-center justify-center w-10 h-10 rounded-full hover:bg-white/10 transition-colors text-white/80 hover:text-white">
              <Globe className="w-5 h-5" />
            </button>
            <button className="flex items-center justify-center w-10 h-10 rounded-full hover:bg-white/10 transition-colors text-white/80 hover:text-white relative">
              <Bell className="w-5 h-5" />
              <span className="absolute top-2.5 right-2.5 w-2 h-2 bg-[#F5A623] rounded-full border-2 border-brand-primary"></span>
            </button>
          </div>

          {authUser ? (
            <ProfileDropdown defaultRole={authUser?.role?.name || "customer"} />
          ) : (
            <>
              <Link
                to="/login"
                className="px-6 h-[52px] flex items-center justify-center bg-[#F5A623] text-white rounded-[14px] hover:bg-[#E09315] transition-colors font-semibold shadow-soft hover:shadow-lg"
              >
                Login
              </Link>
              <Link
                to="/register"
                className="px-6 h-[52px] flex items-center justify-center border-2 border-white/30 text-white rounded-[14px] hover:bg-white/10 transition-colors font-medium bg-white/5 backdrop-blur-sm"
              >
                Register
              </Link>
            </>
          )}
        </div>

        {/* Mobile Toggle Button */}
        <div className="md:hidden">
          <button onClick={toggleNavbar}>{isOpen ? <FaTimes size={24} /> : <FaBars size={24} />}</button>
        </div>
      </div>

      {/* Mobile Navigation */}
      {isOpen && (
        <div className="md:hidden bg-white/95 backdrop-blur-md px-6 py-4 space-y-4 shadow-inner">
          {getMenuItemsForRole().map((item) => (
            <Link key={item.url} to={item.url} className="block text-brand-text font-medium hover:text-brand-primary transition-colors">
              {item.label}
            </Link>
          ))}
          {authUser ? (
            <div className="space-y-2 pt-2 border-t">
              {getDashboardLinks().map((dashboard) => (
                <Link
                  key={dashboard.url}
                  to={dashboard.url}
                  className="block text-gray-700 hover:text-[#ff9906]"
                >
                  {dashboard.label}
                </Link>
              ))}
              <Link to="/account" className="block text-gray-700 hover:text-[#ff9906]">
                Profile Settings
              </Link>
              <button onClick={handleLogout} className="block w-full text-left text-red-600 hover:text-red-700">
                Logout
              </button>
            </div>
          ) : (
            <div className="space-y-2 pt-2 border-t">
              <Link to="/login" className="block text-[#e8a84e] hover:text-[#ff9906]">
                Login
              </Link>
              <Link to="/register" className="block text-[#e8a84e] hover:text-[#ff9906]">
                Register
              </Link>
            </div>
          )}
        </div>
      )}
    </header>
  )
}

export default Header
